/**
 * Cliente do painel do aprovador por link (/aprovar#<token>).
 * Não usa o ApiService (JWT): a autenticação é o token do link (header) + o cookie
 * de sessão httpOnly que o backend grava depois do código do WhatsApp.
 */
import { APP_CONFIG } from '@/utils/constants';

const BASE = `${String(APP_CONFIG.API_URL).replace(/\/$/, '')}/aprovador`;

export class AprovadorError extends Error {
  code: string;
  status: number;
  retryInSeconds?: number;
  constructor(message: string, code: string, status: number, retryInSeconds?: number) {
    super(message);
    this.code = code;
    this.status = status;
    this.retryInSeconds = retryInSeconds;
  }
}

async function call<T>(token: string, method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      credentials: 'include',
      referrerPolicy: 'no-referrer',
      headers: { 'Content-Type': 'application/json', 'X-Approver-Token': token },
      body: method === 'POST' ? JSON.stringify({ token, ...(body as object || {}) }) : undefined,
    });
  } catch {
    throw new AprovadorError('Sem conexão. Verifique a internet e tente de novo.', 'network', 0);
  }
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    const err = json.error || {};
    throw new AprovadorError(err.message || `Erro ${res.status}`, err.code || 'error', res.status, err.retryInSeconds);
  }
  return json.data as T;
}

export interface AcessoInfo {
  verified: boolean;
  name?: string;
  firstName?: string;
  company?: string;
  sentTo?: string;
  retryInSeconds?: number;
  expiresAt?: string;
}

export interface Saldo {
  disponivel?: number;
  limite?: number;
  bloqueado?: number;
  total?: number;
  atualizadoEm?: string;
  cached?: boolean;
  stale?: boolean;
  error?: string;
}

export interface ApiLimit { limit: number; used: number; available: number }

export type DueStatus = 'vencido' | 'hoje' | 'a_vencer' | 'sem_vencimento';

export interface PanelItem {
  id: string;
  status: 'pending' | 'processing';
  kind: 'boleto' | 'pix_chave' | 'pix_copia_cola';
  kindLabel: string;
  subtype: 'pix_chave' | 'pix_qr_estatico' | 'pix_qr_dinamico' | 'pix_qr' | 'boleto' | 'arrecadacao' | string;
  payVia: 'api' | 'app';
  bank: 'sicoob' | 'efi' | null;
  appReason: string | null;
  amount: number;
  dueDate: string | null;
  dueStatus: DueStatus;
  description: string | null;
  beneficiaryName: string | null;
  beneficiaryDocument: string | null;
  pixKey: string | null;
  pixKeyType: string | null;
  barcode: string | null;
  barcodeFormatted: string | null;
  pixPayload: string | null;
  externalId: string | null;
  requestedByName: string | null;
  appName: string | null;
  createdAt: string;
  awaitingConfirmation: boolean;
  reviewedRecipient: { nome: string | null; documento: string | null } | null;
  guiaVencida: boolean;
  duplicateCount?: number;
}

export interface PaidItem {
  id: string;
  kindLabel: string;
  amount: number;
  beneficiaryName: string | null;
  description: string | null;
  dueDate: string | null;
  paidAt: string;
  channel: 'api' | 'app';
  approvedByName: string | null;
  bankDocument: string | null;
  validateUrl: string;
  pdfUrl: string;
}

export interface Panel {
  approver: { name: string; company: string; canPayApi: boolean; canMarkPaid: boolean; canReject: boolean };
  today: string;
  in7days: string;
  saldo: Saldo;
  apiLimit: ApiLimit;
  efiTransfer?: EfiTransfer;
  items: PanelItem[];
  paidToday: PaidItem[];
}

export interface Review {
  id: string;
  valor: number;
  recebedor: { nome: string | null; documento: string | null; chave: string | null };
  descricao: string | null;
  dueDate: string | null;
  bank?: 'sicoob' | 'efi' | null;
  saldo: Saldo;
  /** null quando a Efí paga (sem teto diário da API do Sicoob) */
  apiLimit: ApiLimit | null;
  /** true quando a conferência é de uma transferência para a própria conta Efí */
  transferToEfi?: boolean;
}

export interface EfiTransfer {
  enabled: boolean;
  saldoEfi: Saldo | null;
}

export interface ActionResult {
  ok: boolean;
  status: string;
  error?: string;
  comprovante?: { validateUrl: string; pdfUrl: string; e2e?: string; texto?: string };
}

export const aprovadorApi = {
  acesso: (t: string) => call<AcessoInfo>(t, 'POST', '/acesso'),
  enviarCodigo: (t: string) => call<{ sentTo: string; expiresInMinutes: number; retryInSeconds: number }>(t, 'POST', '/codigo'),
  verificar: (t: string, code: string) => call<AcessoInfo>(t, 'POST', '/verificar', { code }),
  sair: (t: string) => call<void>(t, 'POST', '/sair'),
  painel: (t: string) => call<Panel>(t, 'GET', '/painel'),
  conferir: (t: string, id: string) => call<Review>(t, 'POST', `/itens/${id}/conferir`),
  confirmar: (t: string, id: string) => call<ActionResult>(t, 'POST', `/itens/${id}/confirmar`),
  paguei: (t: string, id: string, body: { bankDocument?: string; paidAmount?: string; attachment?: { filename: string; mimeType: string; data: string } }) =>
    call<ActionResult>(t, 'POST', `/itens/${id}/paguei`, body),
  rejeitar: (t: string, id: string, reason: string) => call<ActionResult>(t, 'POST', `/itens/${id}/rejeitar`, { reason }),
  transferirEfiConferir: (t: string, valor: number) => call<Review>(t, 'POST', '/transferencia-efi', { valor }),
  transferirEfiCancelar: (t: string, id: string) => call<{ ok: boolean; status: string }>(t, 'POST', `/transferencia-efi/${id}/cancelar`),
};

// ---------- formatação ----------
export const brl = (v?: number | null) =>
  v == null || Number.isNaN(v) ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** YYYY-MM-DD → DD/MM/AAAA, sem passar por Date (evita o deslocamento de fuso). */
export const dataBR = (ymd?: string | null) => {
  if (!ymd) return '—';
  const [y, m, d] = ymd.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

export const horaBR = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' }) : '—';

export async function copiar(texto: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    const ta = document.createElement('textarea');
    ta.value = texto;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  }
}
